import { apiService } from "./api.service";
import { formatService } from "./format.service";
import { injectable } from "inversify";
import IPerformanceService from "./interfaces/performance.service.interface";
import { IResponseModel } from "@/models/interfaces/response.model.interface";
import { IPropertyPerformanceSummary } from "@/models/interfaces/performance/property-performance-summary.model.interface";
import { INumberPerformanceSummary } from "@/models/interfaces/performance/number-performance-summary.model.interface";
import { IGroupedPerformanceSummary } from "@/models/interfaces/performance/grouped-performance-summary.model.interface";
import { IStringPerformanceSummary } from "@/models/interfaces/performance/string-performance-summary.model.interface";
import { IDailyPerformanceSummary } from "@/models/interfaces/performance/daily-performance-summary.model.interface";
import { IContentTemplatePerformanceSummary } from "@/models/interfaces/performance/content-template-performance-summary.model.interface";

@injectable()
export default class PerformanceService implements IPerformanceService {
  public async getPropertiesPerformance(startDate: Date, endDate: Date, campaignDeliveryType: number = 1): Promise<IResponseModel<IPropertyPerformanceSummary[]>> {
    return apiService.get(`/api/v1/performance/summary?startDate=${formatService.formatDate(startDate)}&endDate=${formatService.formatDate(endDate)}&campaignDeliveryType=${campaignDeliveryType}`)
    .then((response) => {
            const result = response.data as IResponseModel<IPropertyPerformanceSummary[]>;
            return result;
        })
    .catch((error) => {
            const result: IResponseModel<IPropertyPerformanceSummary[]> = {
                success: false,
                result: []
            };
            return result;
        });
  }

  public async getPropertyPerformance(propertyId: number, startDate: Date, endDate: Date): Promise<IResponseModel<IPropertyPerformanceSummary[]>> {
    return apiService.get(`/api/v1/performance/summary/property/${propertyId}?startDate=${formatService.formatDate(startDate)}&endDate=${formatService.formatDate(endDate)}`)
    .then((response) => {
            const result = response.data as IResponseModel<IPropertyPerformanceSummary[]>;
            return result;
        })
    .catch((error) => {
            const result: IResponseModel<IPropertyPerformanceSummary[]> = {
                success: false,
                result: []
            };
            return result;
        });
  }

  public async getContentSetVersionPerformance(contentSetVersionId: number): Promise<IResponseModel<INumberPerformanceSummary[]>> {
    return apiService.get(`/api/v1/performance/summary/contentset/version/${contentSetVersionId}`)
    .then((response) => {
            const result = response.data as IResponseModel<INumberPerformanceSummary[]>;
            return result;
        })
    .catch((error) => {
            const result: IResponseModel<INumberPerformanceSummary[]> = {
                success: false,
                result: []
            };
            return result;
        });
  }

  public async getPreviousContentSetVersionPerformance(contentSetId: number): Promise<IResponseModel<IGroupedPerformanceSummary[]>> {
    return apiService.get(`/api/v1/performance/summary/contentset/${contentSetId}/version/previous`)
      .then((response) => {
            const result = response.data as IResponseModel<IGroupedPerformanceSummary[]>;
            return result;
        })
      .catch((error) => {
            const result: IResponseModel<IGroupedPerformanceSummary[]> = {
                success: false,
                result: []
            };
            return result;
        });
  }

  public async getPropertyZonePerformance(propertyId: number, startDate: Date, endDate: Date, campaignDeliveryType: number = 1): Promise<IResponseModel<IStringPerformanceSummary[]>> {
      return apiService.get(`/api/v1/performance/summary/property/${propertyId}/zone?startDate=${formatService.formatDate(startDate)}&endDate=${formatService.formatDate(endDate)}&campaignDeliveryType=${campaignDeliveryType}`)
    .then((response) => {
      const result = response.data as IResponseModel<IStringPerformanceSummary[]>;

      if (!result.result) {
          result.result = [];
      }

      return result;
    })
    .catch((error) => {
            const result: IResponseModel<IStringPerformanceSummary[]> = {
                success: false,
                result: []
            };
            return result;
        });
  }

  public async getPropertyTemplatePerformance(propertyId: number, startDate: Date, endDate: Date): Promise<IResponseModel<IContentTemplatePerformanceSummary[]>> {
    return apiService.get(`/api/v1/performance/summary/property/${propertyId}/template?startDate=${formatService.formatDate(startDate)}&endDate=${formatService.formatDate(endDate)}`)
    .then((response) => {
            const result = response.data as IResponseModel<IContentTemplatePerformanceSummary[]>;
            return result;
        })
    .catch((error) => {
            const result: IResponseModel<IContentTemplatePerformanceSummary[]> = {
                success: false,
                result: []
            };
            return result;
        });
  }

  public async getPropertyUrlPerformance(propertyId: number, path: string, startDate: Date, endDate: Date, size: number): Promise<IResponseModel<IStringPerformanceSummary[]>> {
    return apiService.get(`/api/v1/performance/summary/property/${propertyId}/url?size=${size}&startDate=${formatService.formatDate(startDate)}&endDate=${formatService.formatDate(endDate)}&path=${path}`)
    .then((response) => {
      const result = response.data as IResponseModel<IStringPerformanceSummary[]>;

      if (!result.result) {
          result.result = [];
      }

      return result;
    })
    .catch((error) => {
            const result: IResponseModel<IStringPerformanceSummary[]> = {
                success: false,
                result: []
            };
            return result;
        });
  }

  public async getZoneDeviceSummary(propertyId: number, startDate: Date, endDate: Date, campaignDeliveryType: number = 1): Promise<IResponseModel<Map<string, IStringPerformanceSummary[]>>> {
      return apiService.get(`/api/v1/performance/summary/property/${propertyId}/zone/device?startDate=${formatService.formatDate(startDate)}&endDate=${formatService.formatDate(endDate)}&campaignDeliveryType=${campaignDeliveryType}`)
    .then((response) => {
      const result = response.data as IResponseModel<Map<string, IStringPerformanceSummary[]>>;

      if (!result.result) {
          result.result = new Map<string, IStringPerformanceSummary[]>();
      }

      return result;
    })
    .catch((error) => {
            const result: IResponseModel<Map<string, IStringPerformanceSummary[]>> = {
                success: false,
                result: new Map<string, IStringPerformanceSummary[]>()
            };
            return result;
        });
  }

  public async getDailyPropertiesPerformance(startDate: Date, endDate: Date): Promise<IDailyPerformanceSummary> {
    return apiService.get(`/api/v1/performance/daily?startDate=${formatService.formatDate(startDate)}&endDate=${formatService.formatDate(endDate)}`)
    .then((response) => {
            const result = response.data as IDailyPerformanceSummary;

            return result;
        })
    .catch((error) => {
            const result: IDailyPerformanceSummary = {
                keys: [],
                loads: [],
                impressions: [],
                clicks: [],
                keyPresses: []
            };
            return result;
        });
  }

  public async getDailyPropertyPerformance(propertyId: number, startDate: Date, endDate: Date): Promise<IDailyPerformanceSummary> {
    return apiService.get(`/api/v1/performance/daily/property/${propertyId}?startDate=${formatService.formatDate(startDate)}&endDate=${formatService.formatDate(endDate)}`)
    .then((response) => {
            const result = response.data as IDailyPerformanceSummary;

            return result;
        })
    .catch((error) => {
            const result: IDailyPerformanceSummary = {
                keys: [],
                loads: [],
                impressions: [],
                clicks: [],
                keyPresses: []
            };
            return result;
        });
  }
}
